import React, { useEffect, useState } from "react";
import "./RadioButton.scss";
import ClassNames from "classnames";
import TextInput from "../TextInput/TextInput";
import { CUSTOM_EMPTY } from "../RadioButtonsList/RadioButtonsList";
import DateInput from "../DateInput/DateInput";
import FormFieldError from "../FormFieldError/FormFieldError";
import { Tooltip } from "../../components";
import { getDataAnalyticsProps } from "../../../utils";

interface RadioButtonError {
  message?: string;
  type?: string;
}

// TODO: customOption is consumed by still-JS TextInput/DateInput siblings;
// extra fields are spread through, hence the index signature.
interface RadioButtonCustomOption {
  component?: "TEXT" | "DATE";
  onError?: () => void;
  className?: string;
  minDate?: Date | string;
  [key: string]: any;
}

interface RadioButtonTooltip {
  content?: React.ReactNode;
  classname?: string;
}

export interface RadioButtonProps {
  label?: string;
  checked?: boolean;
  error?: RadioButtonError | null;
  className?: string;
  name?: string;
  onChange?: (value: any) => void;
  value?: any;
  disabled?: boolean;
  description?: string;
  leftImg?: string;
  isLarge?: boolean;
  customOption?: RadioButtonCustomOption | null;
  selectedValue?: any;
  itemLabelWidth?: string;
  tabStyle?: boolean;
  pillStyle?: boolean;
  buttonStyle?: boolean;
  "data-testid"?: string;
  withTooltip?: RadioButtonTooltip | null;
  noMargin?: boolean;
  wrapperClassName?: string;
  [key: string]: any;
}

export const defaultProps: Partial<RadioButtonProps> = {
  checked: false,
  label: "",
  error: null,
  className: "",
  name: "",
  onChange: () => {},
  value: null,
  disabled: false,
  leftImg: "",
  isLarge: false,
  customOption: null,
  selectedValue: null,
  itemLabelWidth: "",
  "data-testid": "",
  withTooltip: null,
  buttonStyle: false,
};

const RadioButton: React.FC<RadioButtonProps> = ({
  checked = false,
  label = "",
  error = null,
  className = "",
  name = "",
  onChange = () => {},
  value = null,
  disabled = false,
  description = "",
  leftImg = "",
  isLarge = false,
  customOption = null,
  selectedValue = null,
  itemLabelWidth = "",
  tabStyle,
  pillStyle,
  buttonStyle = false,
  "data-testid": dataTestId = "",
  withTooltip = null,
  noMargin,
  ...props
}) => {
  const classNameWrapper = ClassNames(
    "input-wrap",
    "radio-button-wrap",
    {
      "has-label": label !== null,
      "is-disabled": disabled,
      "is-custom": !!customOption,
      "has-left-img": leftImg,
      "has-error": !!error,
      "is-large": isLarge,
      "has-item-label-width": itemLabelWidth,
      "has-tab-style": tabStyle,
      "has-pill-style": pillStyle,
      "has-button-style": buttonStyle,
      "has-no-margin": noMargin,
    },
    className
  );

  const [customValue, setCustomValue] = useState(
    checked ? selectedValue : null
  );

  useEffect(() => {
    if (selectedValue === CUSTOM_EMPTY) {
      customOption?.onError?.();
    }
  }, [selectedValue]);

  const handleChange = (radioValue: any) => {
    if (radioValue === "custom") {
      onChange(customValue || CUSTOM_EMPTY);
    } else {
      onChange(radioValue);
    }
  };

  const getCustomOption = () => {
    const customValueProps: Record<string, any> = {};
    customValueProps.onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setCustomValue(e?.target?.value);
      handleChange(e?.target?.value);
    };
    customValueProps["data-testid"] = `radio-btn-custom-input`;
    customValueProps.value = customValue;

    switch (customOption?.component) {
      case "TEXT": {
        return <TextInput {...customValueProps} {...customOption} />;
      }
      case "DATE": {
        return (
          <DateInput
            {...customValueProps}
            minDate={customOption?.minDate as string | undefined}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <Tooltip
      type="info"
      content={withTooltip?.content}
      className={withTooltip?.classname}
    >
      <div
        className={`radio-button-input-wrap ${classNameWrapper}`}
        style={{ width: itemLabelWidth }}
      >
        <input
          data-testid={`${dataTestId}-checkbox`}
          className="radio-input"
          id={name}
          name={name}
          type="radio"
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={() => handleChange(value)}
          {...getDataAnalyticsProps(props)}
        />
        {leftImg && (
          <label
            htmlFor={name}
            className={`selection-img ${disabled ? "is-disabled" : ""}`}
            onClick={() => {
              if (!disabled) {
                handleChange(value);
              }
            }}
          >
            <img src={leftImg} height="16" alt={name} />
          </label>
        )}
        {customOption && (
          <label
            htmlFor={name}
            className={`option-item ${customOption?.className} ${
              disabled ? "is-disabled" : ""
            }`}
          >
            {getCustomOption()}
          </label>
        )}
        {label && (
          <label
            htmlFor={name}
            className="radio-button-label"
            onClick={() => {
              if (!disabled) {
                handleChange(value);
              }
            }}
          >
            <span
              className={`label-text ${description ? "has-description" : ""}`}
            >
              {label}
            </span>
            {description && <span>{` - ${description}`}</span>}
          </label>
        )}
        {error && (
          <FormFieldError errorMessage={error.message} errorType={error.type} />
        )}
      </div>
    </Tooltip>
  );
};

export default RadioButton;
